import React, { useEffect, useRef, useState } from 'react'
import '../../css/leaderboardAdSlider.css'
import redZodLeaderboard from '../../media/leaderboard_redzod_v3_728x90.png'
import amoneymatterLeaderboard from '../../media/leaderboard_amoneymatter_728x90.png'
import mazlogicsLeaderboard from '../../media/leaderboard_mazlogics_v1_728x90.png'
import dcliveryLeaderboard from '../../media/leaderboard_dclivery_v1_728x90.png'
import flycomfortLeaderboard from '../../media/leaderboard_flycomforts_v1_728x90.png'
const DEFAULT_SLIDES = [
  {
    href: 'https://www.redzod.com/',
    src: redZodLeaderboard,
    alt: 'Sponsored Ad 1'
  },
  {
    href: 'https://amoneymatter.com/',
    src: amoneymatterLeaderboard,
    alt: 'Sponsored Ad 2'
  },
  {
    href: 'https://www.mazlogics.com/',
    src: mazlogicsLeaderboard,
    alt: 'Sponsored Ad 3'
  },
  {
    href: 'https://www.dclivery.com/',
    src: dcliveryLeaderboard,
    alt: 'Sponsored Ad 4'
  },
  {
    href: 'https://flycomforttravels.com/',
    src: flycomfortLeaderboard,
    alt: 'Sponsored Ad 5'
  }
]

export default function LeaderboardAdSlider({
  slides = DEFAULT_SLIDES,
  intervalMs = 5000,
  className = 'leaderboard-ad-slider'
}) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const pointerStartX = useRef(null)
  const reducedMotion = useRef(false)

  // Respect user motion preferences
  useEffect(() => {
    if (typeof window !== 'undefined') {
      reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  }, [])

  const count = Math.max(0, slides.length)

  const goTo = i => {
    if (!count) return
    const mod = ((i % count) + count) % count
    setIndex(mod)
  }

  const next = () => goTo(index + 1)
  const prev = () => goTo(index - 1)

  // Auto advance
  useEffect(() => {
    if (paused || reducedMotion.current || count <= 1) return
    const id = window.setInterval(() => setIndex(i => (i + 1) % count), Math.max(1500, intervalMs))
    return () => window.clearInterval(id)
  }, [paused, intervalMs, count])

  // Keyboard navigation
  const onKeyDown = e => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      next()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prev()
    }
  }

  // Swipe/drag
  const onPointerDown = e => {
    pointerStartX.current = e.clientX
    setPaused(true)
  }

  const onPointerUp = e => {
    if (pointerStartX.current == null) return
    const dx = e.clientX - pointerStartX.current
    const threshold = 30 // px
    if (Math.abs(dx) > threshold) {
      dx < 0 ? next() : prev()
    }
    pointerStartX.current = null
    setPaused(false)
  }

  const onPointerLeave = () => {
    pointerStartX.current = null
    setPaused(false)
  }

  const defaultRel = 'noopener noreferrer nofollow sponsored'
  const translatePct = count > 0 ? (index * 100) / count : 0

  // Styles (no external CSS needed)
  const outerStyles = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  }

  const frameStyles = {
    position: 'relative',
    width: '100%',
    maxWidth: 970,
    outline: 'none'
  }

  const viewportStyles = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 0,
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
  }

  const ratioPadStyles = { paddingTop: `${(90 / 970) * 100}%` } // 9.278%

  const layerStyles = {
    position: 'absolute',
    inset: 0,
    height: '100%',
    cursor: 'grab'
  }

  const trackStyles = {
    display: 'flex',
    height: '100%',
    width: `${count * 100}%`,
    transform: `translateX(-${translatePct}%)`,
    transition: reducedMotion.current ? undefined : 'transform 500ms ease-in-out',
    willChange: 'transform'
  }

  return (
    <div
      style={outerStyles}
      className={className}
      role='region'
      aria-roledescription='carousel'
      aria-label='Leaderboard Ad Slider'
    >
      <div
        style={frameStyles}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onKeyDown={onKeyDown}
        tabIndex={0}
        aria-live='off'
      >
        <div style={viewportStyles}>
          <div style={ratioPadStyles} />

          {/* Fixed: Added onPointerLeave handler */}
          <div
            style={layerStyles}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
          >
            <div style={trackStyles}>
              {slides.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target={s.target || '_blank'}
                  rel={(s.rel ? s.rel + ' ' : '') + defaultRel}
                  aria-label={`Ad ${i + 1}`}
                  style={{
                    display: 'block',
                    width: `${100 / count}%`,
                    height: '100%',
                    position: 'relative',
                    flexShrink: 0
                  }}
                >
                  <img
                    src={s.src}
                    alt={s.alt || `Advertisement ${i + 1}`}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    draggable={false}
                    loading='eager'
                    decoding='async'
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Dots Indicator */}
        </div>
      </div>
    </div>
  )
}
